import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./CareersCarousel.module.css";

/* Life-at-Coschool photos. */
const SLIDES = [
  {
    src: "/assets/careers/team-collab.jpg",
    alt: "Coschool team members working together on laptops around a table",
  },
  {
    src: "/assets/careers/office.jpg",
    alt: "Open-plan Coschool office with team members at their desks",
  },
  {
    src: "/assets/careers/team-founders-day.jpg",
    alt: "The Coschool team gathered on stage at Founder's Day",
  },
  {
    src: "/assets/careers/football.jpg",
    alt: "Coschool team posing on a football pitch after a match",
  },
  {
    src: "/assets/careers/table-tennis.jpg",
    alt: "A Coschool team member mid-rally at the office table tennis table",
  },
];

const AUTOPLAY_MS = 5000;

export default function CareersCarousel() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  /* Derive the active slide from scroll position so native swipe, the arrows
     and the dots all stay in sync from a single source of truth. */
  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slideWidth = track.clientWidth;
    if (!slideWidth) return;
    const index = Math.round(track.scrollLeft / slideWidth);
    setActive(Math.max(0, Math.min(SLIDES.length - 1, index)));
  }, []);

  const goTo = useCallback((index, smooth = true) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + SLIDES.length) % SLIDES.length;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({
      left: next * track.clientWidth,
      behavior: smooth && !reduced ? "smooth" : "auto",
    });
  }, []);

  useEffect(() => {
    if (paused) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const id = setInterval(() => goTo(active + 1), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [active, paused, goTo]);

  /* Keep the active slide aligned when the viewport is resized — slide offsets
     are pixel-based, so a resize would otherwise leave the track mid-slide. */
  useEffect(() => {
    const onResize = () => goTo(active, false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active, goTo]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(active + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(active - 1);
    }
  };

  return (
    <section
      className={styles.section}
      aria-roledescription="carousel"
      aria-label="Life at Coschool"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className={styles.viewport}>
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowPrev}`}
          onClick={() => goTo(active - 1)}
          aria-label="Previous photo"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M15 5l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div
          className={styles.track}
          ref={trackRef}
          onScroll={handleScroll}
          onKeyDown={onKeyDown}
          tabIndex={0}
          role="group"
          aria-label={`Photo ${active + 1} of ${SLIDES.length}`}
        >
          {SLIDES.map((slide, i) => (
            <div className={styles.slide} key={slide.src}>
              <img
                src={slide.src}
                alt={slide.alt}
                className={styles.img}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                draggable="false"
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowNext}`}
          onClick={() => goTo(active + 1)}
          aria-label="Next photo"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M9 5l7 7-7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.dots}>
        {SLIDES.map((slide, i) => (
          <button
            key={slide.src}
            type="button"
            className={`${styles.dot} ${i === active ? styles.dotActive : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to photo ${i + 1}`}
            aria-current={i === active}
          />
        ))}
      </div>
    </section>
  );
}
